import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SelectedContentContext } from 'src/modules/contentList/contexts/selectedContentContext';
import { useAllPages } from 'src/modules/contentList/hooks/useAllPages';
import { useInvalidatePagesAfterBulkDripping } from 'src/modules/contentList/hooks/useInvalidatePagesAfterBulkDripping';
import { useIsExtraModule } from 'src/modules/contentList/hooks/useIsExtraModule';
import { usePublishStatus } from 'src/modules/contentList/hooks/usePublishStatus';
import { useSelectedContentCount } from 'src/modules/contentList/hooks/useSelectedContentCount';
function selectedContentToArray(selectedContent) {
    return Object.entries(selectedContent).flatMap(([id, selected]) => (selected ? [id] : []));
}
export const SelectedContentProvider = ({ children, modules }) => {
    const [selectedModules, setSelectedModules] = useState({});
    const [selectedPages, setSelectedPages] = useState({});
    const [allIsSelected, setAllIsSelected] = useState(false);
    const pages = useAllPages(modules);
    const isExtraModule = useIsExtraModule();
    useEffect(function clearSelectedOnExtraChange() {
        setAllIsSelected(false);
        setSelectedModules({});
        setSelectedPages({});
    }, [isExtraModule]);
    useEffect(function updateSelectedPagesWhenPagesChange() {
        setSelectedModules((selectedModules) => {
            // Select pages from selected modules
            setSelectedPages((selectedPages) => {
                return pages.reduce((acc, page) => {
                    const isModuleSelected = selectedModules[page.moduleId];
                    const isPageSelected = selectedPages[page.id];
                    if (isModuleSelected || isPageSelected)
                        return { ...acc, [page.id]: true };
                    return acc;
                }, {});
            });
            return selectedModules;
        });
    }, [pages]);
    useEffect(function updateSelectedModulesWhenModulesChange() {
        setSelectedModules((selectedModules) => {
            const newSelectedModules = (modules ?? []).reduce((acc, module) => {
                return selectedModules[module.id] ? { ...acc, [module.id]: true } : acc;
            }, {});
            if (Object.keys(newSelectedModules).length < (modules ?? []).length) {
                setAllIsSelected(false);
            }
            return newSelectedModules;
        });
    }, [modules]);
    const selectModule = useCallback((moduleId, selected) => {
        setSelectedModules((selectedModules) => {
            // Update allIsSelected
            if (selected) {
                const everyModuleIsSelected = modules?.every((module) => module.id === moduleId || selectedModules[module.id]) ??
                    false;
                setAllIsSelected(everyModuleIsSelected);
            }
            else {
                setAllIsSelected(false);
            }
            // Update module
            return { ...selectedModules, [moduleId]: selected };
        });
        // Update module pages
        setSelectedPages((selectedPages) => {
            return pages.reduce((acc, page) => ({
                ...acc,
                [page.id]: page.moduleId === moduleId ? selected : selectedPages[page.id]
            }), {});
        });
    }, [pages, modules]);
    const selectPage = useCallback((moduleId, pageId, selected) => {
        setSelectedPages((selectedPages) => {
            setSelectedModules((selectedModules) => {
                if (selected) {
                    // Update allIsSelected
                    const everyOtherModulePageSelected = pages.every((page) => page.moduleId !== moduleId || page.id === pageId || selectedPages[page.id]);
                    if (selected) {
                        setAllIsSelected(modules?.every((module) => module.id === moduleId
                            ? selected && everyOtherModulePageSelected
                            : selectedModules[module.id]) ?? false);
                    }
                    else {
                        setAllIsSelected(false);
                    }
                    // Select module
                    if (everyOtherModulePageSelected) {
                        return { ...selectedModules, [moduleId]: true };
                    }
                }
                // Unselect module
                if (!selected) {
                    setAllIsSelected(false);
                    return { ...selectedModules, [moduleId]: false };
                }
                return selectedModules;
            });
            // Select page
            return { ...selectedPages, [pageId]: selected };
        });
        if (!selected) {
            setAllIsSelected(false);
        }
    }, [pages, modules]);
    const selectAll = useCallback((selected) => {
        setAllIsSelected(selected);
        if (selected) {
            modules?.forEach((module) => selectModule(module.id, selected));
        }
        else {
            setSelectedModules({});
            setSelectedPages({});
        }
    }, [modules, selectModule]);
    useInvalidatePagesAfterBulkDripping({ pages, selectedPages, selectAll });
    // Check if module has at least one selected page
    const moduleIsIndeterminate = useCallback((moduleId) => {
        return (!selectedModules[moduleId] &&
            pages.some((page) => page.moduleId === moduleId && selectedPages[page.id]));
    }, [selectedModules, selectedPages, pages]);
    const { hasPublished, hasUnpublished } = usePublishStatus({
        pages,
        selectedModules,
        selectedPages
    });
    const selectedContentCount = useSelectedContentCount({
        modules: modules ?? [],
        pages,
        selectedModules,
        selectedPages
    });
    const selectedModuleIds = useMemo(() => selectedContentToArray(selectedModules), [selectedModules]);
    const selectedPageIds = useMemo(() => selectedContentToArray(selectedPages), [selectedPages]);
    return (_jsx(SelectedContentContext.Provider, { value: {
            selectModule,
            selectPage,
            selectAll,
            moduleIsIndeterminate,
            selectedModules,
            selectedPages,
            allIsSelected,
            hasPublished,
            hasUnpublished,
            selectedContentCount,
            selectedPageIds,
            selectedModuleIds
        }, children: children }));
};
